// SupplierPicker — typeahead with inline create-supplier modal.
//
// Per Ace (2026-04-27 feedback): free-text supplier names create
// duplicates from typos and trailing spaces. Per Ace (follow-up):
// adding "Phyto-Therapy" via Contacts didn't make it appear here —
// because Contacts and Suppliers were two separate tables.
//
// Fix: this picker reads primarily from `contacts` (category='Supplier')
// — that's the user-facing list staff actually maintain. Any rows still
// living only in the legacy `suppliers` table are merged in as a fallback.
// When the user picks one, the picker transparently finds-or-creates a
// matching `suppliers` row so the catalogue's `supplier_id` FK stays
// valid. When the user creates a new supplier inline, BOTH a `contacts`
// row AND a `suppliers` row are written.
//
// Caller contract:
//   <SupplierPicker
//     session={session}
//     value={{ id, name }}                // current selection (id = supplier_id)
//     onChange={({ id, name }) => …}     // fires when user picks/creates
//   />
//
// The picker stays controlled — `value` is the source of truth. If the
// caller has only a legacy `supplier_name` (text) with no `supplier_id`,
// pass { id: null, name: legacyName } and the picker shows the name as
// "(unmatched — pick or create)" until the user resolves it.

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { aws } from '../lib/awsdb';
import { authWrite } from '../lib/authWrite';
import { useToast } from '../lib/Toast';
import { humanizeError } from '../lib/humanizeError';
import ContactAutocomplete from '../lib/ContactAutocomplete';

// Contact categories that should appear in the supplier picker. Most
// staff tag suppliers explicitly as "Supplier"; "Service Provider"
// sometimes overlaps for things like co-packers and 3PLs.
const SUPPLIER_CATEGORIES = ['Supplier', 'Service Provider'];

export default function SupplierPicker({ session, value, onChange, disabled }) {
  const toast = useToast();
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createSeed, setCreateSeed] = useState('');
  const [resolving, setResolving] = useState(false);
  const wrapRef = useRef(null);

  // Load from contacts (where category is supplier-like) AND the legacy
  // suppliers table, then merge by case-insensitive name. Each entry's
  // `_origin` records where it came from so the picker can dispatch
  // pick() correctly.
  const load = async () => {
    const [{ data: contacts }, { data: legacy }] = await Promise.all([
      aws.from('contacts')
        .select('id, company_name, contact_person, email, category')
        .in('category', SUPPLIER_CATEGORIES)
        .is('archived_at', null)
        .order('company_name'),
      aws.from('suppliers')
        .select('id, name, contact_email, default_currency')
        .order('name'),
    ]);
    const seen = new Map();
    for (const c of contacts || []) {
      if (!c.company_name) continue;
      const key = c.company_name.trim().toLowerCase();
      if (seen.has(key)) continue;
      seen.set(key, {
        _origin: 'contact',
        contact_id: c.id,
        name: c.company_name,
        contact_person: c.contact_person,
        email: c.email,
      });
    }
    for (const s of legacy || []) {
      if (!s.name) continue;
      const key = s.name.trim().toLowerCase();
      if (seen.has(key)) {
        // Merge — store the supplier_id on the existing entry so pick()
        // can short-circuit the find-or-create step.
        const existing = seen.get(key);
        existing.supplier_id = s.id;
        existing.email = existing.email || s.contact_email || null;
        continue;
      }
      seen.set(key, {
        _origin: 'supplier',
        supplier_id: s.id,
        name: s.name,
        email: s.contact_email,
      });
    }
    setSuppliers(Array.from(seen.values()).sort((a, b) =>
      (a.name || '').localeCompare(b.name || '')));
  };
  useEffect(() => { load(); }, []);

  // Close dropdown on outside click.
  useEffect(() => {
    function onDoc(e) {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return suppliers.slice(0, 12);
    return suppliers
      .filter(s => (s.name || '').toLowerCase().includes(q))
      .slice(0, 12);
  }, [suppliers, query]);

  const exactMatch = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return suppliers.find(s => (s.name || '').trim().toLowerCase() === q) || null;
  }, [suppliers, query]);

  // When the user picks an entry, ensure a `suppliers` row exists for
  // the FK target on forecaster_catalog. If we already know the
  // supplier_id (legacy entry or merged contact), we're done. Otherwise
  // we look up by case-insensitive name and create one if missing,
  // copying contact fields where available.
  async function pick(entry) {
    setQuery('');
    setOpen(false);
    if (entry.supplier_id) {
      onChange?.({ id: entry.supplier_id, name: entry.name });
      return;
    }
    // Resolve via name — find existing or create.
    setResolving(true);
    try {
      const { data: existing } = await aws.from('suppliers')
        .select('id, name')
        .ilike('name', entry.name)
        .limit(1);
      let supplierId = existing?.[0]?.id;
      if (!supplierId) {
        const rows = await authWrite(session, 'suppliers', 'insert', {
          name: entry.name,
          contact_name: entry.contact_person || null,
          contact_email: entry.email || null,
          default_currency: 'AUD',
        });
        const created = Array.isArray(rows) ? rows[0] : rows;
        supplierId = created?.id;
      }
      if (!supplierId) throw new Error('Could not resolve supplier');
      onChange?.({ id: supplierId, name: entry.name });
      // Reload so the entry now carries the supplier_id (avoids
      // re-resolving next time the same contact is picked).
      load();
    } catch (e) {
      const { title, body } = humanizeError(e, 'Could not link supplier');
      toast.error(title, body);
    } finally {
      setResolving(false);
    }
  }

  function openCreate(seed) {
    setCreateSeed(seed.trim());
    setCreateOpen(true);
    setOpen(false);
  }

  // The parent passes value={{ id, name }}. If id is null but name is
  // present, the supplier is unmatched legacy data — flag it visually.
  const displayLabel = value?.name
    ? (value.id
        ? value.name
        : `${value.name} (unmatched — pick or create)`)
    : '';

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        className="form-input"
        type="text"
        disabled={disabled}
        value={open ? query : displayLabel}
        onFocus={() => { setQuery(''); setOpen(true); }}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        placeholder="Search or type a new supplier name…"
      />
      {open && !disabled && (
        <div style={dropdownStyle}>
          {filtered.length > 0 && (
            <>
              {filtered.map(s => {
                const key = s.contact_id ? `c:${s.contact_id}` : `s:${s.supplier_id}`;
                return (
                  <button
                    key={key}
                    type="button"
                    disabled={resolving}
                    onClick={() => pick(s)}
                    style={dropdownRowStyle}
                    onMouseEnter={e => e.currentTarget.style.background = '#F4EFE6'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ fontWeight: 500 }}>{s.name}</span>
                    {s.email && (
                      <span style={{ fontSize: 11, color: '#8B9094', marginLeft: 8 }}>{s.email}</span>
                    )}
                    {s._origin === 'contact' && !s.supplier_id && (
                      <span style={{ fontSize: 10, color: '#00A86B', marginLeft: 8, fontFamily: '"JetBrains Mono", monospace' }} title="From Contacts; will link to the suppliers list when picked.">
                        contacts
                      </span>
                    )}
                  </button>
                );
              })}
            </>
          )}
          {query.trim() && !exactMatch && (
            <button
              type="button"
              onClick={() => openCreate(query)}
              style={{ ...dropdownRowStyle, color: '#00A86B', borderTop: '1px solid #F4EFE6', fontWeight: 500 }}
              onMouseEnter={e => e.currentTarget.style.background = '#E8F4EE'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              + Create supplier "{query.trim()}"
            </button>
          )}
          {value?.id && (
            <button
              type="button"
              onClick={() => { onChange?.({ id: null, name: '' }); setQuery(''); setOpen(false); }}
              style={{ ...dropdownRowStyle, color: '#8B9094', borderTop: '1px solid #F4EFE6', fontSize: 12 }}
              onMouseEnter={e => e.currentTarget.style.background = '#FBF1E6'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              × Clear selection
            </button>
          )}
          {!query.trim() && filtered.length === 0 && (
            <div style={{ padding: 12, color: '#8B9094', fontSize: 12 }}>
              No suppliers yet — type a name to create one.
            </div>
          )}
        </div>
      )}
      {createOpen && (
        <CreateSupplierModal
          session={session}
          seed={createSeed}
          onCancel={() => setCreateOpen(false)}
          onCreated={async (created) => {
            await load();
            onChange?.({ id: created.id, name: created.name });
            setCreateOpen(false);
            toast.success('Supplier added', `${created.name} is now in your supplier list.`);
          }}
          onError={(title, body) => toast.error(title, body)}
        />
      )}
    </div>
  );
}

function CreateSupplierModal({ session, seed, onCancel, onCreated, onError }) {
  const [form, setForm] = useState({
    name: seed || '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    default_currency: 'AUD',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && !saving) onCancel(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, saving]);

  async function save() {
    const name = form.name.trim();
    if (!name) {
      onError('Name required', 'Supplier needs a name.');
      return;
    }
    setSaving(true);
    try {
      // Write to suppliers (the FK target). Existing UNIQUE on
      // suppliers.name will surface a clean 23505 if the user typed
      // a name that already exists.
      const supplierRows = await authWrite(session, 'suppliers', 'insert', {
        name,
        contact_name: form.contact_name.trim() || null,
        contact_email: form.contact_email.trim() || null,
        contact_phone: form.contact_phone.trim() || null,
        default_currency: form.default_currency || 'AUD',
        notes: form.notes.trim() || null,
      });
      const created = Array.isArray(supplierRows) ? supplierRows[0] : null;
      if (!created) throw new Error('No row returned');

      // Mirror to contacts so it shows up everywhere staff manage
      // counterparties (Contacts page, ContactDrawer, future cases).
      // Only inserted if no contact already has the same company_name
      // — case-insensitive trim — so re-runs don't dupe.
      const { data: existing } = await aws.from('contacts')
        .select('id').ilike('company_name', name).limit(1);
      if (!existing || existing.length === 0) {
        try {
          await authWrite(session, 'contacts', 'insert', {
            company_name: name,
            contact_person: form.contact_name.trim() || null,
            email: form.contact_email.trim() || null,
            phone: form.contact_phone.trim() || null,
            category: 'Supplier',
            notes: form.notes.trim() || null,
            created_by: session?.code || null,
          });
        } catch (mirrorErr) {
          // Best-effort — supplier write already succeeded. Log but
          // don't block the user.
          console.warn('[SupplierPicker] contact mirror failed', mirrorErr);
        }
      }

      onCreated(created);
    } catch (e) {
      const { title, body } = humanizeError(e, 'Could not save supplier');
      onError(title, body);
    } finally {
      setSaving(false);
    }
  }

  return createPortal(
    <div
      role="dialog" aria-modal="true"
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,20,25,0.42)',
        backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 200,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 'min(480px, 92vw)',
          background: '#FBF7EE', border: '1px solid #E2DDD2',
          borderRadius: 14, boxShadow: '0 30px 60px rgba(15,20,25,0.18)',
          padding: '22px 24px', fontFamily: 'Inter, system-ui, sans-serif',
        }}
      >
        <div style={{
          fontFamily: 'JetBrains Mono, monospace', fontSize: 10.5,
          letterSpacing: '0.14em', textTransform: 'uppercase',
          color: '#8B9094', marginBottom: 6,
        }}>New supplier</div>
        <h3 style={{
          fontFamily: 'Tiempos, Georgia, serif', fontSize: 22, fontWeight: 400,
          color: '#0F1419', margin: '0 0 16px 0', letterSpacing: '-0.005em',
        }}>Add supplier</h3>

        <Field label="Name *">
          <input className="form-input" autoFocus value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Contact name">
            <ContactAutocomplete
              session={session}
              value={form.contact_name || ''}
              onChange={(name) => setForm(f => ({ ...f, contact_name: name }))}
              placeholder="Search or type contact name…"
            />
          </Field>
          <Field label="Currency">
            <select className="form-select" value={form.default_currency}
              onChange={e => setForm(f => ({ ...f, default_currency: e.target.value }))}>
              {['AUD', 'USD', 'EUR', 'GBP', 'NZD', 'CNY', 'SGD', 'JPY'].map(c =>
                <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Email">
            <input className="form-input" type="email" value={form.contact_email}
              onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} />
          </Field>
          <Field label="Phone">
            <input className="form-input" value={form.contact_phone}
              onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))} />
          </Field>
        </div>
        <Field label="Notes">
          <textarea className="form-textarea" rows={2} value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
        </Field>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button type="button" onClick={onCancel} disabled={saving}
            className="btn btn-secondary btn-sm">Cancel</button>
          <button type="button" onClick={save} disabled={saving || !form.name.trim()}
            className="btn btn-primary btn-sm">
            {saving ? 'Saving…' : 'Add supplier'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label className="form-label" style={{ fontSize: 11 }}>{label}</label>
      {children}
    </div>
  );
}

const dropdownStyle = {
  position: 'absolute',
  top: 'calc(100% + 4px)', left: 0, right: 0,
  background: '#FAFAF8', border: '1px solid #E2DDD2',
  borderRadius: 10, boxShadow: '0 6px 16px rgba(15,20,25,0.08)',
  maxHeight: 280, overflowY: 'auto',
  zIndex: 50,
};

const dropdownRowStyle = {
  display: 'block', width: '100%', textAlign: 'left',
  background: 'transparent', border: 'none',
  padding: '8px 12px', cursor: 'pointer',
  fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13,
  color: '#0F1419',
};
